import { useEffect, useState } from "react";

// Product tour built from real screenshots of the app (apps/web/public/tour/,
// regenerated by e2e/tour-capture.capture.ts against a seeded sample workspace).
// Captions describe only what is on screen.

type Shot = { src: string; alt: string; phone?: boolean };
type Stop = { key: string; label: string; title: string; points: string[]; shots: Shot[] };

const stops: Stop[] = [
  {
    key: "crew", label: "Crew app", title: "Each trimmer logs their own work on a phone.",
    points: [
      "Each person signs in with their own invite: scan the QR code on the manager’s screen or open the link they’re sent.",
      "Home shows today’s grams against their target, their streak, level and what they’ve earned today.",
      "A run timer tracks active trim time with pause and stop, and a live pace check against their own average.",
      "Saving a run shows the weight, yield, active time, any personal best and progress toward the job.",
    ],
    shots: [
      { src: "/tour/crew-invite.jpg", alt: "Personal invite screen: Hi Jordan, join North Room", phone: true },
      { src: "/tour/crew-home.jpg", alt: "Crew home with today’s target ring, streak, earnings and level", phone: true },
      { src: "/tour/crew-trim.jpg", alt: "Trim timer running with pace versus average, pause and stop", phone: true },
      { src: "/tour/crew-saved.jpg", alt: "Saved screen with personal best, QA, weight, yield and job progress", phone: true },
    ],
  },
  {
    key: "today", label: "Live room", title: "See who’s trimming and how the day is going.",
    points: [
      "Live now lists everyone mid-run, with the option to cancel a session.",
      "Today’s runs with grams, g/min, duration, harvest and yield.",
      "Totals for runs, grams, hours, average g/min and average QA, plus a 30-day output chart.",
    ],
    shots: [{ src: "/tour/dash-today.jpg", alt: "Manager dashboard with two live sessions, today’s runs and totals" }],
  },
  {
    key: "records", label: "Runs", title: "Every run, by trimmer and by harvest.",
    points: [
      "The full run list with QA score, grams, g/min and duration. Managers can edit or delete runs.",
      "By trimmer: runs, total grams, time, average g/min and average QA for each person.",
      "By harvest: output, time, average yield and QA for each harvest, open or closed.",
    ],
    shots: [
      { src: "/tour/dash-runs.jpg", alt: "Recent runs list with QA scores and edit and delete buttons" },
      { src: "/tour/dash-trimmers.jpg", alt: "By trimmer table for five crew members" },
      { src: "/tour/dash-byharvest.jpg", alt: "By harvest table for an open and a closed harvest" },
    ],
  },
  {
    key: "jobs", label: "Harvests & jobs", title: "Set a target and watch it fill.",
    points: [
      "Create harvests; new runs attach to the newest open harvest automatically.",
      "Trim jobs with a gram target show progress, current g/min, an ETA and how many crew are on it.",
    ],
    shots: [
      { src: "/tour/dash-jobs.jpg", alt: "Trim jobs with progress toward their gram targets and ETAs" },
      { src: "/tour/dash-harvests.jpg", alt: "Harvest list with close and reopen" },
    ],
  },
  {
    key: "pay", label: "Pay & contracts", title: "Know what everyone is owed.",
    points: [
      "Turn pay on per person. Hourly rates use active trim time; a piece rate pays per gram.",
      "Add bonuses, then mark runs and bonuses paid. Paid amounts are locked in.",
      "Contracts: “trim 3,000 g for $900”, owed when the target is reached. An optional daily bonus only counts on days where every run passes your QA minimum.",
    ],
    shots: [
      { src: "/tour/pay.jpg", alt: "Pay tab with total owed and one trimmer’s hourly, piece and bonus totals" },
      { src: "/tour/pay-contract.jpg", alt: "Contract card with progress toward 3,000 g and daily bonus days earned, under target and awaiting QA" },
    ],
  },
  {
    key: "board", label: "Leaderboard", title: "A crew board, if you want one.",
    points: [
      "Today, 7-day and all-time boards by total weight, with average and best g/min, filterable by harvest.",
      "A Most Improved board and a TV mode for a screen in the room.",
      "Hidden from crew by default. The owner chooses whether everyone can see it.",
    ],
    shots: [{ src: "/tour/board.jpg", alt: "7-day leaderboard with medals for the top three" }],
  },
  {
    key: "room", label: "Trim room", title: "Batches and bags, accounted for.",
    points: [
      "Open a batch, prepare bags and issue them to crew.",
      "A running ledger of intake, available and in-progress weight, with CSV export and a printable report.",
    ],
    shots: [{ src: "/tour/trim-room.jpg", alt: "Trim room batch with bag queue and weight ledger" }],
  },
  {
    key: "console", label: "Console", title: "Set up once, then get out of the way.",
    points: [
      "Add crew and send each person their own invite link or QR code. Archive someone in a click.",
      "Piece rate, leaderboard visibility, API tokens for your own tools, and billing in one place.",
    ],
    shots: [
      { src: "/tour/console.jpg", alt: "Owner console with plan, room screen link and piece rate" },
      { src: "/tour/console-crew.jpg", alt: "Crew list with Invite and Archive buttons" },
      { src: "/tour/console-settings.jpg", alt: "Leaderboard visibility, API access and change password" },
    ],
  },
];

const AUTOPLAY_MS = 6000;

export function ProductTour({ expanded = false }: { expanded?: boolean }) {
  const [view, setView] = useState(stops[0].key);
  const [index, setIndex] = useState(0);
  // Autoplay until the visitor takes control; hovering or focusing the tour only pauses it.
  const [driven, setDriven] = useState(false);
  const [paused, setPaused] = useState(false);
  const stopIndex = stops.findIndex((s) => s.key === view);
  const stop = stops[stopIndex];
  const shotIndex = Math.min(index, stop.shots.length - 1);
  const shot = stop.shots[shotIndex];
  const choose = (key: string) => { setView(key); setIndex(0); };
  // Next/previous screen, rolling over into the neighbouring tab.
  const step = (dir: 1 | -1) => {
    const i = shotIndex + dir;
    if (i >= 0 && i < stop.shots.length) { setIndex(i); return; }
    const next = stops[(stopIndex + dir + stops.length) % stops.length];
    setView(next.key);
    setIndex(dir === 1 ? 0 : next.shots.length - 1);
  };
  const take = (fn: () => void) => () => { setDriven(true); fn(); };

  useEffect(() => {
    if (driven || paused || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = window.setTimeout(() => step(1), AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  });

  return <div className={`product-tour ${expanded ? "tour-expanded" : ""}`} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
    <div className="tour-caption"><span className="tour-caption-dot" />Real screens from the app<span className="sample-label">Sample data</span></div>
    <div className="tour-window">
      <div className="tour-tabs" role="tablist" aria-label="Product tour">
        {stops.map(({ key, label }, i) => <button key={key} type="button" role="tab" id={`tour-tab-${key}`} aria-selected={view === key} aria-controls={`tour-panel-${key}`} tabIndex={view === key ? 0 : -1} onClick={take(() => choose(key))} onKeyDown={(event) => {
          if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          setDriven(true);
          const next = event.key === "Home" ? 0 : event.key === "End" ? stops.length - 1 : (i + (event.key === "ArrowRight" ? 1 : -1) + stops.length) % stops.length;
          choose(stops[next].key);
          document.getElementById(`tour-tab-${stops[next].key}`)?.focus();
        }}>{label}</button>)}
      </div>
      <div className="tour-content tour-shot-layout" role="tabpanel" id={`tour-panel-${view}`} aria-labelledby={`tour-tab-${view}`}>
        <div className="tour-copy">
          <h3>{stop.title}</h3>
          <ul>{stop.points.map((p) => <li key={p}>{p}</li>)}</ul>
          {stop.shots.length > 1 && <div className="tour-shot-pager" role="group" aria-label="Screens">
            {stop.shots.map((s, i) => <button key={s.src} type="button" aria-label={`Screen ${i + 1} of ${stop.shots.length}`} aria-pressed={shot === s} className={shot === s ? "active" : ""} onClick={take(() => setIndex(i))} />)}
          </div>}
        </div>
        <div className="tour-stage">
          <button type="button" className="tour-arrow tour-arrow-prev" aria-label="Previous screen" onClick={take(() => step(-1))}>‹</button>
          <button type="button" className={`tour-shot ${shot.phone ? "tour-shot-phone" : ""}`} aria-label="Next screen" title="Next screen" onClick={take(() => step(1))}>
            <img src={shot.src} alt={shot.alt} />
          </button>
          <button type="button" className="tour-arrow tour-arrow-next" aria-label="Next screen" onClick={take(() => step(1))}>›</button>
        </div>
      </div>
    </div>
    {expanded && <p className="tour-disclaimer">Screenshots of the real app with a sample workspace. Names and numbers are made up.</p>}
  </div>;
}
